import React from 'react';
import { Link } from 'react-router-dom';
import { DocumentTextIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

const Terms: React.FC = () => {
  return (
    <div className="min-h-screen bg-yellow-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-black hover:text-yellow-600 mb-6 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center mb-4">
            <div className="h-12 w-12 bg-black rounded-lg flex items-center justify-center mr-4">
              <DocumentTextIcon className="w-8 h-8 text-yellow-400" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-extrabold text-black">
                Terms and Conditions
              </h1>
              <p className="text-gray-600 mt-2">Last updated: January 2026</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-black mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              By accessing or using the SS Electronics website ("Site"), you agree to be bound by these Terms and Conditions ("Terms"). If you disagree with any part of these terms, then you may not access the Site.
            </p>
            <p className="text-gray-700 leading-relaxed">
              These Terms apply to all visitors, users, and others who access or use the Site. Your access to and use of the Site is also conditioned on your acceptance of and compliance with our Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mb-4">2. Use License</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Permission is granted to temporarily download one copy of the materials on SS Electronics' website for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Modify or copy the materials</li>
              <li>Use the materials for any commercial purpose or for any public display (commercial or non-commercial)</li>
              <li>Attempt to reverse engineer any software contained on the Site</li>
              <li>Remove any copyright or other proprietary notations from the materials</li>
              <li>Transfer the materials to another person or "mirror" the materials on any other server</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              This license shall automatically terminate if you violate any of these restrictions and may be terminated by SS Electronics at any time.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mb-4">3. Account Registration</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To access certain features of the Site, you may be required to register for an account. When you register, you agree to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your password and identification</li>
              <li>Accept all responsibility for any and all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mb-4">4. Products and Pricing</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 Product Information</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We strive to provide accurate product descriptions, images, and pricing. However, we do not warrant that product descriptions, images, or other content on the Site is accurate, complete, reliable, current, or error-free.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.2 Dual Pricing System</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              SS Electronics offers a dual-pricing system for retailers and distributors:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li><strong>Retail Pricing:</strong> Standard pricing available to all customers</li>
              <li><strong>Distributor Pricing:</strong> Special pricing available to verified distributors and bulk purchasers</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              Pricing is subject to change without notice. We reserve the right to modify prices at any time, but such changes will not affect orders already placed.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">4.3 Payment Terms</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              All prices are in the currency specified on the Site. Payment must be made at the time of purchase unless other arrangements have been made for business accounts. We accept major credit cards and other payment methods as indicated on the Site.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mb-4">5. Orders and Shipping</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Order Acceptance</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Your order is an offer to purchase products from us. We reserve the right to accept or reject your order for any reason, including product availability, errors in pricing or product information, or fraud prevention.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">5.2 Shipping</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We will ship products to the address you provide during checkout. Shipping costs and estimated delivery times will be displayed during checkout. We are not responsible for delays caused by shipping carriers or customs.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">5.3 Risk of Loss</h3>
            <p className="text-gray-700 leading-relaxed">
              All items purchased from SS Electronics are made pursuant to a shipment contract. This means that the risk of loss and title for such items pass to you upon our delivery to the carrier.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mb-4">6. Returns and Refunds</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We want you to be satisfied with your purchase. Our return and refund policy is as follows:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Returns must be initiated within 30 days of delivery</li>
              <li>Products must be in original, unused condition with all packaging and accessories</li>
              <li>Custom or personalized items may not be eligible for return</li>
              <li>Refunds will be processed to the original payment method within 5-10 business days</li>
              <li>Shipping costs for returns are the responsibility of the customer unless the product is defective or incorrect</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              To initiate a return, please contact our customer service team with your order number and reason for return.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mb-4">7. Intellectual Property</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The Site and its original content, features, and functionality are owned by SS Electronics and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>
            <p className="text-gray-700 leading-relaxed">
              You may not use our trademarks, logos, or other proprietary information without our express written consent.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mb-4">8. Prohibited Uses</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You agree not to use the Site:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>In any way that violates any applicable law or regulation</li>
              <li>To transmit, or procure the sending of, any advertising or promotional material without our prior written consent</li>
              <li>To impersonate or attempt to impersonate the company, a company employee, another user, or any other person or entity</li>
              <li>In any way that infringes upon the rights of others, or in any way is illegal, threatening, fraudulent, or harmful</li>
              <li>To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Site</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mb-4">9. Disclaimer of Warranties</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              THE INFORMATION ON THIS SITE IS PROVIDED ON AN "AS IS" BASIS. TO THE FULLEST EXTENT PERMITTED BY LAW, SS Electronics EXCLUDES ALL REPRESENTATIONS, WARRANTIES, CONDITIONS, AND TERMS RELATING TO OUR WEBSITE AND THE USE OF THIS WEBSITE.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We do not warrant that the Site will be available at all times or that it will be error-free, secure, or free from viruses or other harmful components.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mb-4">10. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, SS Electronics SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Our total liability to you for all claims arising from or related to the use of the Site shall not exceed the amount you paid to us in the 12 months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mb-4">11. Indemnification</h2>
            <p className="text-gray-700 leading-relaxed">
              You agree to defend, indemnify, and hold harmless SS Electronics and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable attorney's fees, arising out of or in any way connected with your access to or use of the Site or your violation of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mb-4">12. Governing Law</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which SS Electronics operates, without regard to its conflict of law provisions. Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts in that jurisdiction.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mb-4">13. Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              By continuing to access or use our Site after any revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, you are no longer authorized to use the Site.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-black mb-4">14. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about these Terms and Conditions, please contact us:
            </p>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
              <p className="text-gray-800 font-semibold mb-2">SS Electronics</p>
              <p className="text-gray-700">Email: legal@sselectronics.com</p>
              <p className="text-gray-700">Phone: +1 (555) 123-4567</p>
              <p className="text-gray-700">Address: 123 Electronics Street, Tech City, TC 12345</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Terms;

